import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { checkoutsColumns } from '../../utils/definitions.util'
import { CheckoutService } from '../../services/entity-services/checkout.service'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'

const DEFAULT_FILTERS = {
  name: '',
  user: null,
}

@Component({
  selector: 'app-checkouts',
  templateUrl: 'checkouts.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CheckoutsComponent extends BaseListing implements OnInit {
  private checkoutService = inject(CheckoutService)

  listingColumns = signal(checkoutsColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(structuredClone(DEFAULT_FILTERS))

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const f: Record<string, any> = {}
    const value = this.filters()
    if (value.name?.length) f['name'] = value.name
    if (value.user) f['user'] = value.user
    return f
  })

  readonly queryCheckouts = injectInfiniteQuery(() => ({
    queryKey: ['checkouts', this.sortProp(), this.sortDir()],
    queryFn: async ({ pageParam }) => {
      // Persist current view settings before fetching, per tags.component.ts pattern
      return await this.checkoutService
        .getCheckouts(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          { id: null, name: null, enabled: null }
        )
        .then(result => {
          this.saveViewSettings('checkouts', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  checkouts = computed(() => this.queryCheckouts.data()?.pages?.flatMap(p => p.items) ?? [])
  totalItems = computed(() => this.queryCheckouts.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryCheckouts.data()
    })
  }

  ngOnInit() {
    const view = this.loadViewSettings('checkouts')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    // this.filters.set(this.loadViewSettings('checkouts') || structuredClone(DEFAULT_FILTERS))

    // default for salesman: show just their checkouts unless user already set a filter
    if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.filters().user) {
      this.filters.update(v => ({ ...v, user: this.core.services.user.user?.id ?? null }))
    }

    this.loadSorting('checkouts')
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    if (this.core.services.user.hasRole('ROLE_SALESMAN')) {
      this.filters.update(v => ({ ...v, user: this.core.services.user.user?.id ?? null }))
    }
    this.queryCheckouts.refetch()
  }

  // Immutable update for name filter to ensure signals/computed re-run
  onNameFilterChange(name: string) {
    this.filters.update(v => ({ ...v, name: name ?? '' }))
  }
}
